import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { LoadingService } from '../../services/LoadingService';
import { PageHeaderComponent } from '../page-header/page-header.component';

@Component({
  selector: 'app-admin-test',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
    PageHeaderComponent
  ],
  templateUrl: './admin-test.component.html',
  styleUrl: './admin-test.component.css'
})
export class AdminTestComponent {
  isTestRunning = false;
  testProgress = 0;
  testResults: string[] = [];

  constructor(private loadingService: LoadingService) {}

  /**
   * Test the global loading component for 100 seconds
   */
  testGlobalLoading(): void {
    if (this.isTestRunning) {
      return;
    }

    this.isTestRunning = true;
    this.testProgress = 0;
    this.testResults = [];

    this.addTestResult('🚀 Starting global loading test...');

    // Start the global loading
    this.loadingService.start('http');
    this.addTestResult('✅ Global loading started');

    // Update progress every 100ms for 100 seconds
    const progressInterval = setInterval(() => {
      this.testProgress += 1;

      if (this.testProgress % 100 === 0) {
        this.addTestResult(`⏱️ Progress: ${this.testProgress / 10} seconds`);
      }

      if (this.testProgress >= 1000) {
        clearInterval(progressInterval);

        // Stop the global loading
        this.loadingService.end('http');
        this.addTestResult('🛑 Global loading stopped');
        this.addTestResult('✅ Test completed successfully!');

        this.isTestRunning = false;
        this.testProgress = 1000;
      }
    }, 1000);
  }

  /**
   * Test multiple loading sources
   */
  testMultipleLoadingSources(): void {
    if (this.isTestRunning) {
      return;
    }

    this.isTestRunning = true;
    this.testProgress = 0;
    this.testResults = [];

    this.addTestResult('🚀 Starting multiple loading sources test...');

    // Start multiple loading sources
    this.loadingService.start('http');
    this.addTestResult('✅ HTTP loading started');

    setTimeout(() => {
      this.loadingService.start('route');
      this.addTestResult('✅ Route loading started');
      this.testProgress = 25;
    }, 1000);

    setTimeout(() => {
      this.loadingService.start('http'); // Second HTTP request
      this.addTestResult('✅ Second HTTP loading started');
      this.testProgress = 50;
    }, 3000);

    setTimeout(() => {
      this.loadingService.end('http'); // End first HTTP
      this.addTestResult('🛑 First HTTP loading ended');
      this.testProgress = 75;
    }, 6000);

    setTimeout(() => {
      this.loadingService.end('route'); // End route
      this.addTestResult('🛑 Route loading ended');
    }, 8000);

    setTimeout(() => {
      this.loadingService.end('http'); // End second HTTP
      this.addTestResult('🛑 Second HTTP loading ended');
      this.addTestResult('✅ Multiple sources test completed!');
      this.isTestRunning = false;
      this.testProgress = 100;
    }, 10000);
  }

  /**
   * Test rapid loading toggles
   */
  testRapidToggle(): void {
    if (this.isTestRunning) {
      return;
    }

    this.isTestRunning = true;
    this.testProgress = 0;
    this.testResults = [];

    this.addTestResult('🚀 Starting rapid toggle test...');

    let toggleCount = 0;
    const maxToggles = 20;

    const toggleInterval = setInterval(() => {
      if (toggleCount % 2 === 0) {
        this.loadingService.start('http');
        this.addTestResult(`🟢 Loading ON (${Math.floor(toggleCount / 2) + 1})`);
      } else {
        this.loadingService.end('http');
        this.addTestResult(`🔴 Loading OFF (${Math.floor(toggleCount / 2) + 1})`);
      }

      toggleCount++;
      this.testProgress = (toggleCount / maxToggles) * 100;

      if (toggleCount >= maxToggles) {
        clearInterval(toggleInterval);

        // Ensure loading is stopped
        this.loadingService.end('http');
        this.addTestResult('✅ Rapid toggle test completed!');
        this.isTestRunning = false;
        this.testProgress = 100;
      }
    }, 500);
  }

  /**
   * Clear test results
   */
  clearResults(): void {
    this.testResults = [];
    this.testProgress = 0;
  }

  /**
   * Add a test result with timestamp
   */
  private addTestResult(message: string): void {
    const timestamp = new Date().toLocaleTimeString();
    this.testResults.push(`[${timestamp}] ${message}`);
  }
}
